<template>
  <div
    class="line-item"
    :class="{
      [$class('loading')]: !lineItem,
      [$class('small')]: small,
      [$class('updating')]: state === 'updating',
      'is-simple': simple,
    }"
  >
    <div class="line-item__grid">
      <div class="line-item__image-container">
        <a
          :title="lineItem.product_title"
          :href="lineItem.url"
          class="line-item__image-link"
        >
          <responsive-image
            class="line-item__image"
            :image="lineItem.featured_image"
            :lazy-load="false"
            min-max="95-285"
            ratio="3:2"
            sizes="(min-width: 1024px) 205px"
          />
        </a>
      </div>

      <div class="line-item__body">
        <div class="line-item__details">
          <div class="line-item__details-header">
            <a
              class="
                line-item__title
                text-heading-s
              "
              :href="lineItem.url"
              v-text="lineItem.product_title"
            />

            <p
              v-if="(!small && !isMobile) && !simple"
              class="
                line-item__price-container
                line-item__price-container--desktop
                text-body-m-regular
              "
            >
            <product-prices
                class="line-item__price"
                sawmill="line-item__price"
                class-price="text-other-price-small"
                class-compare-at-price="text-other-sale-s"
                element="span"
                :compare-at-price="compareAtPrice"
                :price="lineItem?.final_line_price"
              />
            </p>
          </div>

          <p
            class="text-body-s-regular"
            v-text="`Sku: ${lineItem.sku}`"
          />

          <p
            v-if="compatibilityList.length > 0"
            class="line-item__compatibility text-body-m-regular"
          >
            <span
              class="line-item__compatibility-label"
              v-text="$string('product.sidebar.works_with')"
            />

            <span
              v-for="(compatible, index) of compatibilityList"
              :key="compatible"
            >
              <span
                class="line-item__compatibility-item"
                v-text="compatible"
              />
              <span
                v-if="(compatibilityList.length - 1) > index"
                class="line-item__compatibility-separator"
              >|</span>
            </span>
          </p>

          <p
            v-if="lineItem.properties && lineItem.properties['Ships in']"
            class="line-item__leadtime"
          >
            <span
              v-text="$string('cart.line_item.ships_in')"
            />
            <span
              v-text="lineItem.properties && lineItem.properties['Ships in']"
            />
          </p>
          <p
            v-if="!lineItem.product_has_only_default_variant || lineItem.gift_card"
            class="
              line-item__variant
              text-body-m-regular
            "
            v-text="variantText"
          />

          <p
            class="
              line-item__price-container
              text-body-s-regular
            "
            :class="{ 'text-body-m-regular-desktop': !small }"
          >
            <product-prices
              class="line-item__price"
              class-price="text-other-price-small"
              class-compare-at-price="text-other-sale-s"
              element="span"
              :compare-at-price="compareAtPrice"
              :price="lineItem?.final_line_price"
            />
          </p>

          <p
            v-if="!gift && !simple && lineItem.line_level_discount_allocations?.length"
            class="
              line-item__discounts
              text-body-s-regular
            "
            :class="{ 'text-body-m-regular-desktop': !small }" :style="{'background-color': liquid.discountColor}" 
          >
            <span class="line-item__discount">
              <img v-if="liquid.discountIcon" :src="liquid.discountIcon" class="line-item__icon" alt="icon">

              <span
                v-for="({ discount_application }) of lineItem.line_level_discount_allocations"
                :key="`discount-${discount_application.key}`"
                class="text-body-xs-regular text-body-s-regular-desktop line-item__discount_text" :style="{'color': liquid.discountFontColor  }" >
              {{ liquid.discountLabel }} : {{ discount_application.title }} </span>
            </span>
          </p>
        </div>

        <div
          v-if="(!small && !isMobile) && !simple"
          class="line-item__actions line-item__actions--desktop"
        >
          <quantity-selector
            v-if="!gift"
            class="line-item__quantity-selector"
            :line-item="lineItem"
            :quantity="quantity"
            :state="state"
            @update-quantity="handleQuantityUpdate"
          />

          <span
            class="line-item__remove text-link"
            @click="handleRemove"
            v-text="$string('cart.line_item.remove')"
          />
        </div>
      </div>
    </div>

    <div>
      <div
        v-if="(small || isMobile) && !simple"
        class="line-item__actions line-item__actions--mobile"
      >
        <quantity-selector
          v-if="!gift"
          class="line-item__quantity-selector"
          :line-item="lineItem"
          :quantity="quantity"
          :state="state"
          @update-quantity="handleQuantityUpdate"
        />

        <span
          class="line-item__remove text-link"
          @click="handleRemove"
          v-text="$string('cart.line_item.remove')"
        />
      </div>
    </div>

    <notification-panel
      v-if="!simple && notification.display"
      class-container="line-item__notification-panel"
      :show-close="false"
      :text="notification.text"
      :title="notification.title"
      :type="notification.type"
    />
  </div>
</template>

<script>

/**
 * Vue: Line item (line-item)
 * -----------------------------------------------------------------------------
 * Displays line items in cart, cart drawer, and order.
 *
 * @param {Boolean|Object} lineItem - Line item in cart response or order.
 * @param {Boolean} [simple] - Show only image and text, no buttons.
 * @param {Boolean} [small] - Use mobile layout at all breakpoints.
 *
 */
import { mapActions, mapState } from 'vuex'

import NotificationPanel from '~async/utils/notification-panel/notification-panel'
import ProductPrices from '~global/product-prices/product-prices'
import QuantitySelector from '~async/quantity-selector/quantity-selector'
import ResponsiveImage from '~global/images/responsive-image'

import IconTag from '~icons/general/tag.svg'

export default {
  name: 'LineItem',

  components: {
    IconTag,
    NotificationPanel,
    ProductPrices,
    QuantitySelector,
    ResponsiveImage,
  },

  props: {
    lineItem: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    simple: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    liquid:{
      type: Object,
      default: () => ({}),
    }
  },

  data() {
    return {
      quantity: 1,
      state: 'loading',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cartStatus: (state) => state.cart.status,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute compare at price.
     * - Uses compare at price if it exists.
     * - Otherwise if script discount has been applied use original price.
     * - This way it always shows the largest original price.
     * - Otherwise show no compare at price.
     * @returns {Number|Boolean}
     */
    compareAtPrice() {
      if (this.lineItem?.compare_at_price) {
        return this.lineItem?.compare_at_price * this.lineItem?.quantity
      }

      if (this.lineItem?.original_price !== this.lineItem?.final_price) {
        return this.lineItem?.original_price * this.lineItem?.quantity
      }

      return  this.lineItem?.compare_at_price * this.lineItem?.quantity
    },

    /**
     * Compute if line item has __gift property.
     * @returns {Boolean}
     */
    gift() {
      // eslint-disable-next-line no-underscore-dangle
      return Boolean(this.lineItem.properties?.__gift)
    },

    /**
     * Compute if line item is limited by inventory quantity.
     * @returns {Boolean}
     */
    limitedInventory() {
      return (
        this.lineItem.inventory_management &&
        this.lineItem.inventory_policy === 'deny'
      )
    },

    /**
     * Compute notification state.
     * @returns {Object}
     */
    notification() {
      if (!this.lineItem || !this.limitedInventory) {
        return { display: false }
      }

      const notification = {
        type: 'error',
      }

      if (this.lineItem.quantity > this.lineItem.inventory_quantity) {
        notification.display = true
        notification.title = this.$string('cart.notifications.limited_stock.title')

        notification.text = this.$string(
          'cart.notifications.limited_stock.text',
          {
            pluralise: this.lineItem.inventory_quantity,
            replace: { count: this.lineItem.inventory_quantity },
          },
        )

      } else if (this.lineItem.quantity === this.lineItem.inventory_quantity) {
        notification.display = true
        notification.title = this.$string('cart.notifications.maximum_quantity.title')

        notification.text = this.$string(
          'cart.notifications.maximum_quantity.text',
          {
            pluralise: this.lineItem.inventory_quantity,
            replace: { count: this.lineItem.inventory_quantity },
          },
        )
      }

      return notification
    },

    /**
     * Compute variant text.
     * @returns {String}
     */
    variantText() {
      if (this.lineItem.gift_card) {
        return this.$formatMoney(this.lineItem.original_price)
      }

      if (!this.lineItem.options_with_values) {
        return this.lineItem.variant_title
      }

      return this.lineItem.options_with_values.map((option) => {
        return this.$string(
          'cart.line_item.variant',
          {
            replace: {
              option_name: option.name,
              selected_value: option.value,
            },
          },
        )
      }).join(' / ')
    },

    /**
     * Compute compatibility list.
     * @returns {Array}
     */
    compatibilityList() {
      let compatibilityList = []

      if (this.lineItem.properties && this.lineItem.properties['Works with']) {
        compatibilityList = this.lineItem.properties['Works with'].split('|').map((item) => item.trim())
      }

      return compatibilityList
    },
  },

  watch: {

    /**
     * Watch cart status items to see if item is being updated.
     */
    'cartStatus.items'() {
      if (this.cartStatus.items.includes(this.lineItem.key)) {
        this.state = 'updating'
        return
      }

      this.state = 'ready'
    },

    /**
     * Watch item quantity to update quantity if updated elsewhere.
     */
    'lineItem.quantity'() {
      this.quantity = this.lineItem.quantity
    },
  },

  mounted() {
    this.state = 'ready'
    this.quantity = this.lineItem.quantity
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      updateItem: 'cart/updateItem',
      removeItem: 'cart/updateItem',
    }),

    /**
     * Handle quantity update event.
     * @param {String} quantity - New quantity.
     */
    async handleQuantityUpdate(quantity) {
      this.quantity = quantity

      try {
        this.state = 'updating'

        await this.updateItem([
          {
            id: this.lineItem.key,
            quantity: this.quantity,
          },
        ])

        this.state = 'ready'

      } catch (error) {
        this.quantity = this.lineItem.quantity
        this.state = 'error'
      }
    },

    /**
     * Handle remove click.
     */
    handleRemove() {
      if (this.state === 'updating') {
        return
      }

      this.removeItem(this.lineItem.key)
    },
  },
}
</script>

<style lang="scss">
@import './line-item';
</style>
